import { BaseRetriever, DataCondition, IRetriever, ICondition, IConditionManager } from '@draftkings/widgets-core';
import {
    WidgetZonesRequestResolver,
    SportsbooksEventConstructor,
    Widget,
    SportEvent,
    League,
    SportsbookPushResponse,
    Metadata
} from '@draftkings/dk-data-layer';
import { ClankData, ClankОptions, ConditionKeys } from '../../contracts/retriever';
import { eventDeepMerge } from '../deepMerge';
import { getErrorDescription } from '@draftkings/sportsbook-common-utils';
import { ProductConfig } from '@draftkings/sportsbook-common-contracts';

export class ClankRetriver extends BaseRetriever<void, ClankData> implements IRetriever<ClankData> {
    private widgetZonesRequestResolver: WidgetZonesRequestResolver;
    private SportsbookEvent: SportsbooksEventConstructor;
    private zoneId: string;
    private widgets: Widget[];
    private condition: IConditionManager<typeof ConditionKeys> & ICondition;
    private productConfig: ProductConfig;
    private logError: ClankОptions['logError'];
    private trackEvent: ClankОptions['trackEvent'];

    constructor(options: ClankОptions) {
        super({
            ...options,
            onSuspend: options.widgetZonesRequestResolver.cancel
        });
        this.widgetZonesRequestResolver = options.widgetZonesRequestResolver;
        this.logError = options.logError;
        this.trackEvent = options.trackEvent;
        this.condition = options.condition;
        this.condition.set('Retriever', options.zoneId ? DataCondition.LOADING : DataCondition.LOADED);
        this.SportsbookEvent = options.SportsbookEvent;
        this.widgets = options.widgets;
        this.zoneId = options.zoneId;
        this.productConfig = options.productConfig;
    }

    protected subscribe = () => {
        const ids = this.widgets.map((widget) => `'${widget.eventId}'`).join(',');
        let subscription;
        if (ids) {
            subscription = new this.SportsbookEvent(
                {
                    entity: 'events',
                    query: `$filter=id in (${ids})`,
                    includeMarkets: 'none'
                },
                'event',
                false,
                this.onDataCallback,
                this.onErrorCallback
            );
            this.trackSocketEvent('SUBSCRIBE', ids);
        }

        return () => {
            this.trackSocketEvent('UNSUBSCRIBE', ids);
            subscription?.deactivate();
        };
    };

    protected getQuery = (): void => undefined;

    protected getData = async (): Promise<ClankData> => {
        this.widgetZonesRequestResolver.cancel();
        if (this.zoneId) {
            try {
                const data = await this.widgetZonesRequestResolver.send({
                    queryParams: { zones: this.zoneId }
                });
                if (data) {
                    this.condition.set('Retriever', DataCondition.LOADED);
                    return {
                        events: new Map<string, SportEvent>(data.events.map((event) => [event.id, event])),
                        leagues: new Map<string, League>(data.leagues.map((league) => [league.id, league]))
                    };
                }
            } catch (error) {
                this.condition.set('Retriever', DataCondition.ERROR);
                this.logError({
                    statusCode: 'FETCH_WIDGET_ZONES_ERROR',
                    description: getErrorDescription(error),
                    id: this.zoneId,
                    widgetVersion: APP_VERSION
                });
            }
        }
        return this.data;
    };

    private onErrorCallback = (error: unknown) => {
        this.logError({
            statusCode: 'SocketConnectionError',
            description: getErrorDescription(error),
            id: this.widgets.map((widget) => `'${widget.eventId}'`).join(','),
            widgetVersion: APP_VERSION
        });
    };

    private onDataCallback = (response: SportsbookPushResponse) => {
        this.add(response.data.add, response.metadata);
        this.change(response.data.change, response.metadata);
        this.remove(response.data.remove, response.metadata);
    };

    private add = (payload: SportsbookPushResponse['data']['add'], metaData: SportsbookPushResponse['metadata']) => {
        payload.events.forEach((event) => {
            const { id } = event;
            const newEvent = { ...event };
            this.data.events.set(id, { ...newEvent });
            this.trackSocketEvent('ADD_EVENT', newEvent.id, metaData);
        });
    };

    private change = (payload: SportsbookPushResponse['data']['change'], metadata) => {
        payload.events.forEach((newEvent) => {
            const existEvent = this.data.events.get(newEvent.id);
            if (existEvent) {
                eventDeepMerge(existEvent, {
                    ...newEvent
                });
                this.trackSocketEvent('UPDATE_EVENT', newEvent.id, metadata);
            }
        });
    };

    private remove = (
        payload: SportsbookPushResponse['data']['remove'],
        metaData: SportsbookPushResponse['metadata']
    ) => {
        payload.events.forEach((event) => {
            const existingEvent = this.data.events.get(event);
            if (existingEvent) {
                existingEvent.status = 'FINISHED';
                this.trackSocketEvent('REMOVE_EVENT', event, metaData);
            }
        });
    };

    private trackSocketEvent(clientAction: string, eventId?: string, metadata?: Metadata) {
        if (!this.productConfig.isEventPushTelemetryEnabled) {
            return;
        }

        const receivedAt = new Date().getTime();
        const publishedAt = metadata?.publishedTime ? new Date(metadata.publishedTime).getTime() : 0;
        const delta = publishedAt ? receivedAt - publishedAt : 0;
        this.trackEvent('LONGSHOT_EVENT', {
            clientAction,
            delta,
            zoneId: this.zoneId,
            eventId: eventId,
            pageName: 'HOME_PAGE',
            widget: 'SPORT_SLIDER_WIDGET',
            widgetVersion: APP_VERSION
        });
    }
}
